import { PAGINGATION_DEFAULT } from '@const/pagination'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import moment from 'moment'
import { fetchNotificationList, fetchSingle } from './asyncAction'

const initialState: TNotificationManagement.TState = {
  notificationList: {
    data: [],
    ...PAGINGATION_DEFAULT
  },
  isFetchingNotificationList: true,
  notificationDetail: {
    data: {
      id: '',
      content: '',
      notificationLinks: [] as TNotificationManagement.TNotificationLink[],
      enqueueTime: null,
      canSchedule: false,
      enqueueDate: '',
      enqueueHour: 9,
      status: 0
    }
  },
  errors: [
    { key: 'type', message: '' },
    { key: 'content', message: '' },
    { key: 'links', message: '' },
    { key: 'enqueueTime', message: '' }
  ],
  isLoading: false
}
const notificationManagementSlice = createSlice({
  name: 'notificationManagement',
  initialState,
  reducers: {
    setChecked (state, action: PayloadAction<{ index: number, isChecked: boolean }>) {
      const { index, isChecked } = action.payload
      state.notificationList.data[index].isChecked = isChecked
    },
    addLinkRow (state) {
      state.notificationDetail.data.notificationLinks.push({ text: '', url: '' })
    },
    removeLinkRow (state, action) {
      state.notificationDetail.data.notificationLinks.splice(action.payload.index, 1)
    },
    changeLinkText (state, action) {
      state.notificationDetail.data.notificationLinks[action.payload.index].text = action.payload.text
    },
    changeLinkUrl (state, action) {
      state.notificationDetail.data.notificationLinks[action.payload.index].url = action.payload.url
    },
    resetAddData () {
      return initialState
    },
    handleChangeInsertMessage (state, action) {
      state.notificationDetail.data.content = action.payload.content
    },
    addError (state, action) {
      const error = state.errors.filter(x => x.key === action.payload.key)[0]
      error.message = action.payload.message
    },
    setIsLoading (state, action) {
      state.isLoading = action.payload
    },
    showFullContent (state, action) {
      const notificationItem = state.notificationList.data.filter(x => x.id === action.payload)[0]
      if (notificationItem) {
        notificationItem.content = notificationItem.fullContent
      }
    },
    resetFormValue (state) {
      state.notificationDetail = initialState.notificationDetail
    },
    setCanSchedule (state, action) {
      state.notificationDetail.data.canSchedule = action.payload
    },
    setEnqueueDate (state, action) {
      const enqueueDateTimeValue = new Date(action.payload)
      enqueueDateTimeValue.setHours(state.notificationDetail.data.enqueueHour)
      state.notificationDetail.data.enqueueTime = moment(enqueueDateTimeValue).format()
      state.notificationDetail.data.enqueueDate = moment(action.payload).format('YYYY-MM-DD')
    },
    setEnqueueTime (state, action) {
      const { enqueueTime } = state.notificationDetail.data
      if (enqueueTime) {
        const enqueueDateTimeValue = new Date(enqueueTime)
        enqueueDateTimeValue.setHours(action.payload)
        state.notificationDetail.data.enqueueTime = moment(enqueueDateTimeValue).format()
      }
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchNotificationList.fulfilled, (state, action: PayloadAction<TNotificationManagement.TState['notificationList']>) => {
        if (!action.payload?.data) return
        state.notificationList = {
          ...action.payload
        }
        state.isFetchingNotificationList = false
      })
      .addCase(fetchNotificationList.pending, state => {
        state.isFetchingNotificationList = true
      })
    builder
      .addCase(fetchSingle.fulfilled, (state, action: PayloadAction<TNotificationManagement.TState['notificationDetail']>) => {
        if (!action.payload) return
        state.notificationDetail = action.payload
        state.isLoading = false
        const { enqueueTime } = state.notificationDetail.data
        if (enqueueTime) {
          const dateTimeValue = new Date(enqueueTime)
          state.notificationDetail.data.enqueueDate = moment(dateTimeValue).format('YYYY-MM-DD')
          state.notificationDetail.data.enqueueHour = dateTimeValue.getHours()
        } else {
          state.notificationDetail.data.enqueueHour = 9
        }
      })
      .addCase(fetchSingle.pending, state => {
        state.isLoading = true
      })
  }
})

export * from './asyncAction'

export const {
  setChecked,
  addLinkRow,
  removeLinkRow,
  changeLinkText,
  changeLinkUrl,
  resetAddData,
  addError,
  handleChangeInsertMessage,
  setIsLoading,
  showFullContent,
  resetFormValue,
  setCanSchedule,
  setEnqueueDate,
  setEnqueueTime
} = notificationManagementSlice.actions

export const notificationManagementReducer = notificationManagementSlice.reducer
